import type { ConditionBuild, GetConditionBuild } from "@skilitics/nautilus-core-designer-data";

import type { DesignerCondition } from "../../types/index.ts";
import type { ResolveConditionBuildables } from "../querying/index.ts";

interface Options {
  getConditionBuild: GetConditionBuild;
  resolveConditionBuildables: ResolveConditionBuildables;
}
export const getConditionCreate =
  ({ getConditionBuild, resolveConditionBuildables }: Options) =>
  (hostNodeId: number, responseId: number): DesignerCondition | undefined => {
    const buildables = resolveConditionBuildables(hostNodeId, { includeChildren: true });

    const buildDesignerCondition = ({
      id,
      nodeId,
      buildableName,
      config,
      children: childBuilds,
    }: ConditionBuild): DesignerCondition | undefined => {
      const buildable = buildables.find((b) => b.name === buildableName && b.nodeId === nodeId);
      if (!buildable) {
        console.warn(`Condition Buildable not found on nodeId ${nodeId} with name ${buildableName}.`);
        return undefined;
      }
      const { builder, spec } = buildable;
      const children = Object.fromEntries(
        Object.entries(childBuilds).map(([name, childOrChilds]) => [
          name,
          Array.isArray(childOrChilds)
            ? childOrChilds.map((conditionBuild) => buildDesignerCondition(conditionBuild)).filter(isDesignerCondition)
            : childOrChilds
              ? buildDesignerCondition(childOrChilds)
              : undefined,
        ]),
      );

      // isValid should default to true unless a condition builder explicitly defines why it is invalid.
      let isValid = true;
      if (builder.isValid) {
        isValid = builder.isValid(config);
      }

      const childBuildables = Object.fromEntries(
        Object.entries(buildable.children)
          .filter((e): e is [string, NonNullable<(typeof e)[1]>] => !!e[1])
          .map(([name, b]) => [name, { ...b, nodeId }]),
      );
      return {
        id,
        builder,
        spec,
        config: structuredClone(config),
        children,
        buildables: childBuildables,
        isValid,
      };
    };

    const conditionBuild = getConditionBuild(responseId);
    if (!conditionBuild) return undefined;
    const condition = buildDesignerCondition(conditionBuild);
    if (!isDesignerCondition(condition)) return undefined;
    return condition;
  };

export type GetCondition = ReturnType<typeof getConditionCreate>;

const isDesignerCondition = (v: DesignerCondition | DesignerCondition[] | undefined): v is DesignerCondition =>
  Boolean(v);

export const findNestedConditionById = (
  conditions: DesignerCondition[],
  conditionId: number,
): DesignerCondition | undefined => {
  for (const condition of conditions) {
    if (condition.id === conditionId) {
      return condition;
    } else if (condition.children) {
      const childConditions = Object.values(condition.children)
        .flat()
        .filter((c): c is DesignerCondition => !!c);

      const matchCondition = findNestedConditionById(childConditions, conditionId);
      if (matchCondition) {
        return matchCondition;
      }
    }
  }
  return undefined;
};
